import React, { ReactElement, useContext, useMemo, useCallback } from 'react';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { ClinicFormTemplateFragment } from 'shared/types/graphql';
import { useHistory } from 'react-router-dom';
import { FormsContext } from '../context/FormsContext';
import {
  Icon,
  Text,
  Heading,
  Box,
  Flex,
  useDisclosure,
  useMediaQuery,
  Menu,
  MenuItemProps,
  Tooltip,
} from '@televet/kibble-ui';
import { DeviceSizes } from 'shared/enums/DeviceSizes';
import FormBadges from './FormBadges';
import DeleteFormModal from '../components/DeleteFormModal';
import useForms from '../hooks/useForms';
import getFormDeleteDisabledMessages, {
  GetFormDeleteDisabledMessagesResultWithReason,
} from '../utils/getFormDeleteDisabledMessages';
import { LinkedForm } from '.';

interface FormListItemProps {
  formTemplate: ClinicFormTemplateFragment;
  linkedAutomations?: LinkedForm[];
  clinicId: string | undefined;
}

const FormListItem = ({ formTemplate, linkedAutomations }: FormListItemProps): ReactElement => {
  const { formId, dispatch } = useContext(FormsContext);
  const history = useHistory();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { addForm, toggleActive } = useForms();
  const [isDesktop] = useMediaQuery('(' + DeviceSizes.TabletMinWidth + ')');

  const selectTemplate = (): void => {
    history.push(`/forms/${formTemplate.id}`);
    dispatch({
      type: 'TOGGLE_TEMPLATE_SELECTOR',
      payload: { isTemplateSelectorShown: false },
    });
  };

  const handleToggleActive = useCallback((): void => {
    toggleActive(formTemplate.id, !formTemplate.isActive);
    dispatch({
      type: 'RESET_NEW_FORM',
      payload: {},
    });
  }, [dispatch, toggleActive, formTemplate]);

  const disabledMessages = useMemo(() => {
    const { formTemplateType, clinicWidgetRequestTypes } = formTemplate;
    return getFormDeleteDisabledMessages<GetFormDeleteDisabledMessagesResultWithReason>({
      formTemplateType,
      clinicWidgetRequestTypes,
      hasLinkedAutomations: Boolean(linkedAutomations?.length),
      includeReason: true,
    });
  }, [formTemplate, linkedAutomations]);

  const menuItems: MenuItemProps[] = useMemo(() => {
    return [
      {
        label: (
          <Tooltip label={disabledMessages.archive}>
            <Box>{formTemplate.isActive ? 'Archive Form' : 'Unarchive Form'}</Box>
          </Tooltip>
        ),
        isDisabled: Boolean(disabledMessages.archive),
        value: formTemplate.id,
        onSelect: (): void => handleToggleActive(),
      },
      {
        label: 'Duplicate Form',
        value: formTemplate.id,
        onSelect: async (): Promise<void> => {
          try {
            await addForm(formTemplate.title + ' Copy', formTemplate.content);
          } catch (err) {
            console.error(err);
          }
        },
      },
      {
        label: (
          <Tooltip label={disabledMessages.delete}>
            <Box>Delete Form</Box>
          </Tooltip>
        ),
        isDisabled: Boolean(disabledMessages.delete),
        value: formTemplate.id,
        onSelect: (): void => onDeleteOpen(),
      },
    ];
  }, [
    addForm,
    disabledMessages.archive,
    disabledMessages.delete,
    formTemplate.content,
    formTemplate.id,
    formTemplate.isActive,
    formTemplate.title,
    handleToggleActive,
    onDeleteOpen,
  ]);

  return (
    <>
      <DeleteFormModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        formTemplateId={formTemplate.id}
        formTemplateName={formTemplate.title}
      />
      <Box
        id={'form-template-' + formTemplate.id}
        onClick={selectTemplate}
        m={3}
        p="10px"
        cursor="pointer"
        borderRadius={8}
        bg="background.default"
        border="3px solid"
        _hover={{
          borderColor: formTemplate.id === formId ? 'border.focus' : 'border.default',
        }}
        borderColor={formTemplate.id === formId ? 'border.focus' : 'transparent'}
        transition="all 0.1s ease-in-out"
      >
        <Flex>
          <Flex>
            <Icon size="xl" name="form" />
          </Flex>
          <Flex flex="1 1 auto" ml="10px" w={isDesktop ? '220px' : '160px'} direction="column">
            <Heading as="h2" isTruncated size="sm">
              {formTemplate.title}
            </Heading>
            <Text size="sm" isTruncated variant="subtle">
              Last modified on: {format(parseISO(formTemplate.updatedAt || formTemplate.createdAt), 'LLL do yyyy')}
            </Text>
          </Flex>
          <Menu
            buttonProps={{ variant: 'ghostNeutral', size: 'sm', showRightIcon: false, leftIconName: 'dotsVertical' }}
            listProps={{ menuItems }}
          />
        </Flex>
        <FormBadges formTemplate={formTemplate} linkedAutomations={linkedAutomations} />
      </Box>
    </>
  );
};

export default FormListItem;
